import { Entity, User } from '../types/apiTypes';

/**
 * Carrier identity — the MC/DOT question the heads-up page (THT-729) asks once, right after login.
 *
 * "Already collected" means exactly one thing: we hold an identifier. It used to also count an
 * answered ownership question, on the reasoning that a lessee running under someone else's
 * authority has no number of their own — but a leased-on owner-operator still ROLLS under
 * somebody's MC, and Highway has nothing to check without it. Counting that answer as collected
 * let a customer finish signup with both columns null, and the carrier-details workflow then
 * failed on the Review transition with nothing on screen to fix (THT-752). The heads-up No path
 * now asks for the number too, and this predicate is what makes Business Info ask again as a
 * backstop for anyone who slipped through before it did.
 *
 * Shared by the heads-up gate in `pages/signup.tsx` and the Business Info slim-down so the two
 * can never drift apart.
 */

/**
 * Takes the whole `User` union and narrows internally rather than asking every caller to prove
 * it holds a customer first. Only the customer branch declares these columns, so a vendor,
 * partner or driver record has no carrier identity by construction — same answer the old
 * structural check gave, without the cast.
 *
 * `owns_their_mc_dot_number` deliberately does not appear: the ownership answer says WHOSE
 * authority the number belongs to, never whether we have one. See the note above the module.
 */
export const customerHasCarrierIdentity = (
  userRecord: User | null | undefined
): boolean => {
  if (userRecord?.userType !== Entity.Customer) return false;
  return Boolean(userRecord.motor_carrier_number || userRecord.dot_number);
};

/**
 * The identity we already hold, in the FORM's field names rather than the column names — what
 * Business Info has to put back on screen for a customer who asks to correct it (THT-711).
 *
 * `carrier_type` is not a stored column. It only ever decided which input the form showed, so it
 * is read back off whichever identifier the record actually carries, and falls back to MC for a
 * lessee who has neither — the same default the field has always opened on.
 */
export type SavedCarrierIdentity = {
  carrier_type: 'MC' | 'DOT';
  mc_number?: string;
  dot_number?: string;
  owns_their_mc_dot_number: boolean | null;
  other_mc_dot_number_owner_name?: string;
};

/**
 * Narrows the `User` union the same way `customerHasCarrierIdentity` does, so a non-customer
 * record resolves to null rather than an empty identity that would render as a blank summary.
 *
 * Empty strings are normalised to `undefined` so the caller can test presence with a plain
 * truthiness check, and a missing ownership answer to `null` — a legacy record can hold a number
 * with that question never asked, and the difference decides whether the revealed form opens
 * with an answer or with the question still to answer.
 */
export const readSavedCarrierIdentity = (
  userRecord: User | null | undefined
): SavedCarrierIdentity | null => {
  if (userRecord?.userType !== Entity.Customer) return null;
  const mcNumber = userRecord.motor_carrier_number || undefined;
  const dotNumber = userRecord.dot_number || undefined;
  return {
    carrier_type: !mcNumber && dotNumber ? 'DOT' : 'MC',
    mc_number: mcNumber,
    dot_number: dotNumber,
    owns_their_mc_dot_number: userRecord.owns_their_mc_dot_number ?? null,
    other_mc_dot_number_owner_name:
      userRecord.other_mc_dot_number_owner_name || undefined,
  };
};

/**
 * The carrier-identity inputs as react-hook-form knows them. Note `mc_number`, not
 * `motor_carrier_number`: the form names this field after the question it asks, and the PATCH
 * renames it to the column it lands in — which is the one place these two lists diverge.
 *
 * Used to `unregister` the block when Business Info stops rendering it, so its required-field
 * rules don't block submit on inputs that are no longer on screen.
 */
export const CARRIER_IDENTITY_FORM_FIELDS = [
  'mc_number',
  'dot_number',
  'carrier_type',
  'owns_their_mc_dot_number',
  'other_mc_dot_number_owner_name',
] as const;

/**
 * The same identity as `PATCH /customers` column names — what must be absent from the payload
 * once the heads-up page owns the answer, so a saved MC/DOT is never overwritten with blanks.
 *
 * `carrier_type` has never been part of the PATCH body (it only drives which input the form
 * shows). It is listed anyway as a forward guard: it is a carrier-identity answer, so if someone
 * ever adds it to the payload it must land inside the conditional spread with the rest.
 */
export const CARRIER_IDENTITY_PATCH_KEYS = [
  'motor_carrier_number',
  'dot_number',
  'carrier_type',
  'owns_their_mc_dot_number',
  'other_mc_dot_number_owner_name',
] as const;

/**
 * The one-shot flag the Review transition drops when the backend rejects the carrier identity
 * (400 `invalidIdentifier`), read on the way into Business Info to raise its warning and put the
 * identifier fields back on screen. Cleared by the PATCH that answers it.
 *
 * Named here rather than repeated as a literal in the three places that touch it — the handler
 * in `AccountInfo`, the reader in `BusinessInfo`, and the heads-up gate in `pages/signup.tsx`,
 * which has to stand aside so the bounce lands where its toast says it does.
 */
export const CARRIER_NUMBER_ERROR_STORAGE_KEY = 'carrierNumberError';
